import {
  Building,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Filter,
  Loader2,
  Search
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

interface Court {
  domstolKod: string;
  domstolNamn: string;
}

interface SearchResult {
  id: string;
  case_number: string;
  court: string;
  court_code: string;
  date: string;
  title: string;
  summary: string;
  type: string;
  is_guiding: boolean;
  keywords: string[];
  published_time: string;
  attachments: any[];
}

interface SearchResponse {
  total: number;
  publications: SearchResult[];
}

interface PublicationDetails {
  id: string;
  benamning: string;
  arVagledande: boolean;
  domstol: {
    domstolNamn: string;
  };
  malNummerLista: string[];
  avgorandedatum: string;
  ecliNummer?: string;
  publiceringstid: string;
  sammanfattning: string;
  innehall: string;
  nyckelordLista: string[];
  lagrumLista: Array<{
    sfsNummer: string;
    referens: string;
  }>;
  bilagaLista: Array<{
    fillagringId: string;
    filnamn: string;
  }>;
}

const SearchDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('all-courts');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [onlyGuiding, setOnlyGuiding] = useState(false);
  const [courts, setCourts] = useState<Court[]>([]);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedPublication, setSelectedPublication] =
    useState<PublicationDetails | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [downloadingAttachments, setDownloadingAttachments] = useState<
    Set<string>
  >(new Set());
  const [loadingPublications, setLoadingPublications] = useState<Set<string>>(
    new Set()
  );

  const perPage = 20;

  // Load courts on component mount
  useEffect(() => {
    loadCourts();
  }, []);

  // Helper function to get the correct API base URL
  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined') {
      // In browser environment, check if we're in development
      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      ) {
        return 'http://localhost:8000';
      }
      // In production, use same origin
      return window.location.origin;
    }
    return 'http://localhost:8000';
  };

  const loadCourts = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/chat/api/search/courts`, {
        method: 'GET',
        credentials: 'include'
      });
      if (response.ok) {
        const courtsData = await response.json();
        setCourts(courtsData);
      } else {
        // Fallback to mock data if API fails
        const mockCourts: Court[] = [
          { domstolKod: 'HD', domstolNamn: 'Högsta domstolen' },
          { domstolKod: 'RegR', domstolNamn: 'Regeringsrätten' },
          { domstolKod: 'HovR', domstolNamn: 'Hovrätten' },
          { domstolKod: 'TR', domstolNamn: 'Tingsrätten' },
          { domstolKod: 'KamR', domstolNamn: 'Kammarrätten' },
          { domstolKod: 'ForvR', domstolNamn: 'Förvaltningsrätten' }
        ];
        setCourts(mockCourts);
      }
    } catch (error) {
      console.error('Failed to load courts:', error);
      // Fallback to mock data on network error
      const mockCourts: Court[] = [
        { domstolKod: 'HD', domstolNamn: 'Högsta domstolen' },
        { domstolKod: 'RegR', domstolNamn: 'Regeringsrätten' },
        { domstolKod: 'HovR', domstolNamn: 'Hovrätten' },
        { domstolKod: 'TR', domstolNamn: 'Tingsrätten' },
        { domstolKod: 'KamR', domstolNamn: 'Kammarrätten' },
        { domstolKod: 'ForvR', domstolNamn: 'Förvaltningsrätten' }
      ];
      setCourts(mockCourts);
    }
  };

  const performSearch = async (page: number = 0) => {
    setLoading(true);
    setError(null);
    setCurrentPage(page);

    try {
      const baseUrl = getApiBaseUrl();
      const params = new URLSearchParams({
        search_term: searchTerm,
        page: page.toString(),
        per_page: perPage.toString()
      });

      if (selectedCourt && selectedCourt !== 'all-courts') {
        params.append('court_codes', selectedCourt);
      }

      if (fromDate) {
        params.append('from_date', fromDate);
      }

      if (toDate) {
        params.append('to_date', toDate);
      }

      if (onlyGuiding) {
        params.append('is_guiding', 'true');
      }

      const response = await fetch(
        `${baseUrl}/chat/api/search/search?${params}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setError(errorMessage);

      // If external API is not available, show a mock result
      if (
        errorMessage.includes('timeout') ||
        errorMessage.includes('network')
      ) {
        setResults({
          total: 1,
          publications: [
            {
              id: 'mock-1',
              case_number: 'T 1234-23',
              court: 'Stockholms tingsrätt',
              court_code: 'ST',
              date: '2023-12-01',
              title:
                'Exempelavgörande - Systemet demonstrerar sökfunktionaliteten',
              summary:
                'Detta är ett exempel för att visa hur sökresultaten presenteras när externa API:er inte är tillgängliga.',
              type: 'Dom',
              is_guiding: false,
              keywords: ['exempel', 'demonstration'],
              published_time: '2023-12-15',
              attachments: []
            }
          ]
        });
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (pubId: string) => {
    // Set loading state
    setLoadingPublications((prev) => new Set([...prev, pubId]));

    try {
      if (pubId === 'mock-1') {
        // Show mock publication details for demo
        const mockPublication: PublicationDetails = {
          id: 'mock-1',
          benamning:
            'Exempelavgörande - Systemet demonstrerar sökfunktionaliteten',
          arVagledande: false,
          domstol: {
            domstolNamn: 'Stockholms tingsrätt'
          },
          malNummerLista: ['T 1234-23'],
          avgorandedatum: '2023-12-01',
          ecliNummer: 'ECLI:SE:ST:2023:1234',
          publiceringstid: '2023-12-15',
          sammanfattning:
            'Detta är ett exempel för att visa hur detaljerad information om avgöranden presenteras. Funktionen demonstrerar hur systemet hanterar visning av fullständig information.',
          innehall:
            'Detta är innehållet i det mockade avgörandet. Här skulle normalt den fullständiga texten från domstolens avgörande visas. Systemet är redo att hantera riktiga data från svenska domstolar när API:erna är tillgängliga.',
          nyckelordLista: ['exempel', 'demonstration', 'mockdata'],
          lagrumLista: [
            { sfsNummer: '1949:345', referens: '1 kap. 1 §' },
            { sfsNummer: '1970:994', referens: '2 kap. 3 §' }
          ],
          bilagaLista: []
        };
        setSelectedPublication(mockPublication);
        setShowDetailsModal(true);
        return;
      }

      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/chat/api/search/publication/${pubId}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch publication details');
      }

      const data = await response.json();
      setSelectedPublication(data);
      setShowDetailsModal(true);
    } catch (err) {
      alert(
        'Could not fetch details: ' +
          (err instanceof Error ? err.message : 'Unknown error')
      );
    } finally {
      // Remove loading state
      setLoadingPublications((prev) => {
        const newSet = new Set(prev);
        newSet.delete(pubId);
        return newSet;
      });
    }
  };

  const downloadAttachment = async (storageId: string) => {
    // Set loading state
    setDownloadingAttachments((prev) => new Set([...prev, storageId]));

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/chat/api/search/attachment/${storageId}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }

      // Create a blob from the response and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachment-${storageId.split('/').pop()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download attachment. Please try again.');
    } finally {
      // Remove loading state
      setDownloadingAttachments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(storageId);
        return newSet;
      });
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedPublication(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      performSearch(0);
    }
  };

  const totalPages = results ? Math.ceil(results.total / perPage) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Search className="h-6 w-6" />
          Sök rättspraxis
        </h2>
        <p className="text-gray-600 mt-1">
          Sök bland avgöranden från Sveriges domstolar. Lämna tomt för att visa
          de senaste avgörandena.
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="searchTerm">Sökord (fritext)</Label>
                <Input
                  id="searchTerm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Exempel: skadestånd, köpeavtal, prövningstillstånd, fastighetsbildning, vårdnad"
                />
              </div>
              <div className="space-y-2">
                <Label>Domstol</Label>
                <Select value={selectedCourt} onValueChange={setSelectedCourt}>
                  <SelectTrigger>
                    <SelectValue placeholder="Alla domstolar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-courts">Alla domstolar</SelectItem>
                    {courts.map((court) => (
                      <SelectItem
                        key={court.domstolKod}
                        value={court.domstolKod}
                      >
                        {court.domstolNamn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced Filters */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="advanced-filters">
                <AccordionTrigger className="flex items-center gap-2 hover:no-underline">
                  <Filter className="h-4 w-4" />
                  Visa avancerade filter
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <Card className="bg-gray-50">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Avancerade filter
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fromDate">Från datum</Label>
                          <Input
                            id="fromDate"
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="toDate">Till datum</Label>
                          <Input
                            id="toDate"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="onlyGuiding"
                          checked={onlyGuiding}
                          onCheckedChange={(checked) =>
                            setOnlyGuiding(checked as boolean)
                          }
                        />
                        <div>
                          <Label
                            htmlFor="onlyGuiding"
                            className="text-sm font-medium"
                          >
                            Visa endast vägledande avgöranden
                          </Label>
                          <p className="text-xs text-gray-500">
                            Vägledande avgöranden har särskild betydelse för
                            rättstillämpningen
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? 'Söker...' : 'Sök avgöranden'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>Söker...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-red-700">
              Ett fel uppstod vid sökning: {error}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !loading && (
        <>
          {/* Results Summary */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <strong>
                {results.total.toLocaleString('sv-SE')} avgöranden hittades
              </strong>
            </CardContent>
          </Card>

          {/* Results List */}
          {results.publications.length > 0 ? (
            <div className="space-y-4">
              {results.publications.map((pub) => (
                <Card
                  key={pub.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-3">
                      {pub.is_guiding && (
                        <Badge
                          variant="secondary"
                          className="bg-yellow-100 text-yellow-800 border-yellow-200"
                        >
                          VÄGLEDANDE
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className="bg-yellow-50 text-yellow-800 font-bold"
                      >
                        {pub.case_number}
                      </Badge>
                    </div>

                    <div className="text-sm text-gray-600 mb-2">
                      <span className="mr-4">
                        <Building className="h-4 w-4 inline mr-1" />
                        <strong>Domstol:</strong> {pub.court}
                      </span>
                      <span className="mr-4">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        <strong>Datum:</strong> {pub.date}
                      </span>
                      <span>
                        <strong>Typ:</strong> {pub.type || 'N/A'}
                      </span>
                    </div>

                    <div className="font-medium text-gray-900 mb-2">
                      {pub.title}
                    </div>

                    {pub.summary && (
                      <div className="text-gray-700 text-sm mb-3 line-clamp-3">
                        {pub.summary.substring(0, 200)}
                        {pub.summary.length > 200 ? '...' : ''}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => viewDetails(pub.id)}
                        disabled={loadingPublications.has(pub.id)}
                        className="flex items-center gap-1"
                      >
                        {loadingPublications.has(pub.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        Visa fullständigt avgörande
                      </Button>
                      {pub.attachments && pub.attachments.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadAttachment(pub.attachments[0].fillagringId)
                          }
                          disabled={downloadingAttachments.has(
                            pub.attachments[0].fillagringId
                          )}
                          className="flex items-center gap-1"
                        >
                          {downloadingAttachments.has(
                            pub.attachments[0].fillagringId
                          ) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <FileText className="h-4 w-4" />
                          )}
                          Visa dokument
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 py-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => performSearch(currentPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Föregående
                  </Button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum =
                      Math.max(0, Math.min(totalPages - 5, currentPage - 2)) +
                      i;
                    return (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => performSearch(pageNum)}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => performSearch(currentPage + 1)}
                  >
                    Nästa
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8 text-gray-500">
                Inga avgöranden hittades.
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPublication && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={closeDetailsModal}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900 pr-8">
                {selectedPublication.benamning || 'Ingen titel'}
              </h2>
              <Button variant="ghost" size="sm" onClick={closeDetailsModal}>
                ×
              </Button>
            </div>

            {selectedPublication.arVagledande && (
              <Badge
                variant="secondary"
                className="bg-yellow-100 text-yellow-800 border-yellow-200 mb-4"
              >
                VÄGLEDANDE
              </Badge>
            )}

            <Card className="bg-gray-50 mb-4">
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong>Domstol:</strong>{' '}
                    {selectedPublication.domstol?.domstolNamn || 'Okänd'}
                  </div>
                  {selectedPublication.malNummerLista &&
                    selectedPublication.malNummerLista.length > 0 && (
                      <div>
                        <strong>Målnummer:</strong>{' '}
                        {selectedPublication.malNummerLista.join(', ')}
                      </div>
                    )}
                  <div>
                    <strong>Avgörandedatum:</strong>{' '}
                    {selectedPublication.avgorandedatum || 'Inget datum'}
                  </div>
                  {selectedPublication.ecliNummer && (
                    <div>
                      <strong>ECLI:</strong> {selectedPublication.ecliNummer}
                    </div>
                  )}
                  <div>
                    <strong>Publiceringstid:</strong>{' '}
                    {selectedPublication.publiceringstid || 'Inget datum'}
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedPublication.sammanfattning && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Sammanfattning
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {selectedPublication.sammanfattning}
                </p>
              </div>
            )}

            {selectedPublication.innehall && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Innehåll
                </h3>
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto border rounded p-3 bg-white">
                  {(() => {
                    // Strip HTML tags and decode HTML entities
                    const stripHtml = (html: string) => {
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = html;
                      return tempDiv.textContent || tempDiv.innerText || '';
                    };

                    const cleanText = stripHtml(selectedPublication.innehall);
                    const truncatedText = cleanText.substring(0, 2000);
                    return (
                      truncatedText + (cleanText.length > 2000 ? '...' : '')
                    );
                  })()}
                </div>
              </div>
            )}

            {selectedPublication.nyckelordLista &&
              selectedPublication.nyckelordLista.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Nyckelord
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedPublication.nyckelordLista.map(
                      (keyword, index) => (
                        <Badge key={index} variant="secondary">
                          {keyword}
                        </Badge>
                      )
                    )}
                  </div>
                </div>
              )}

            {selectedPublication.lagrumLista &&
              selectedPublication.lagrumLista.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Lagrum
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedPublication.lagrumLista.map((lagrum, index) => (
                      <li key={index} className="text-gray-700">
                        {lagrum.sfsNummer || ''} {lagrum.referens || ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {selectedPublication.bilagaLista &&
              selectedPublication.bilagaLista.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Bilagor
                  </h3>
                  <ul className="space-y-2">
                    {selectedPublication.bilagaLista.map((bilaga, index) => (
                      <li key={index}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadAttachment(bilaga.fillagringId)
                          }
                          disabled={downloadingAttachments.has(
                            bilaga.fillagringId
                          )}
                          className="flex items-center gap-2"
                        >
                          {downloadingAttachments.has(bilaga.fillagringId) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          {bilaga.filnamn}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDashboard;
